import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ChevronUp,
  PlusCircle,
  Info,
  List,
  Search,
  Package,
  ArrowLeft,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { TableCard } from "@/components/ui/table-card";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { toast } from "sonner";
import AddSerialsModal from "./components/AddSerialsModal";
import { ScanBarcode } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import RecordNotFound from "@/core/components/RecordNotFound";
import { API_ENDPOINTS } from "../../api/header";
import apiClient from "../../api/apiClient";
import { useSelector } from "react-redux";
import { all_routes } from "../../Router/all_routes";
import { Spinner } from "@/components/ui/spinner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// Defined outside the component so its type identity is stable across parent re-renders.
// This prevents React from remounting the inner <input> and losing focus.
const EditableCellInput = ({
  value: externalValue,
  onChange,
  type = "text",
  className,
  disabled,
  ...props
}) => {
  const [localValue, setLocalValue] = React.useState(externalValue ?? "");
  const isFocusedRef = React.useRef(false);

  // Only sync from external value when the user is NOT actively editing.
  // This prevents the parent re-render from overwriting what the user is typing.
  React.useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalValue(externalValue ?? "");
    }
  }, [externalValue]);

  return (
    <Input
      {...props}
      type={type}
      className={className}
      disabled={disabled}
      value={localValue}
      onFocus={(e) => {
        isFocusedRef.current = true;
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        isFocusedRef.current = false;
        props.onBlur?.(e);
      }}
      onChange={(e) => {
        setLocalValue(e.target.value);
        onChange?.(e.target.value);
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        props.onKeyDown?.(e);
      }}
    />
  );
};

const OpeningStock = () => {
  const queryClient = useQueryClient();
  const currentStore = useSelector((state) => state.current_store);
  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  const [productItems, setProductItems] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [notes, setNotes] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [hasInventoryRec, setHasInventoryRec] = useState("no_inventory_rec");

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  // Reset page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, hasInventoryRec]);

  // Queries
  const { isFetching: isProductsLoading, data: productsData } = useQuery({
    queryKey: [
      "products-manage-inventory",
      currentStore?.id,
      hasInventoryRec,
      searchTerm,
      currentPage,
      pageSize,
    ],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
        params: {
          store_id: currentStore?.id,
          page: currentPage,
          perPage: pageSize,
          search: searchTerm,
          has_inventory_rec: hasInventoryRec,
          manage_inventory: true,
          with: [
            "brand",
            "model",
            "unit",
            "categories",
            "batches",
            "serial_numbers",
          ],
        },
      });

      return {
        data: res.data.data || [],
        total: res.data.meta?.total || res.data.total || 0,
        last_page:
          res.data.meta?.last_page ||
          res.data.last_page ||
          Math.max(
            1,
            Math.ceil(
              (res.data.meta?.total || res.data.total || 0) / pageSize,
            ),
          ),
      };
    },
    enabled: !!currentStore?.id && hasPermission("product read"),
    keepPreviousData: true,
  });

  // Initialize product items when data is fetched
  useEffect(() => {
    if (productsData && productsData.data) {
      const items = productsData.data.map((product) => {
        let isDisabled =
          product.batches?.length > 0 ||
          product.serial_numbers?.length > 0 ||
          Boolean(product.inventory_id) ||
          Boolean(product.inventory) ||
          (Array.isArray(product.inventories) &&
            product.inventories.some(
              (inv) =>
                !currentStore?.id ||
                String(inv.store_id) === String(currentStore.id),
            ));
        let disabledReason = isDisabled ? "Already has inventory record" : "";

        if (!isDisabled) {
          if (
            product.tracking_type !== "standard" &&
            product.tracking_type !== "serial"
          ) {
            isDisabled = true;
            disabledReason = `Invalid tracking: ${product.tracking_type || "none"}`;
          }
        }

        return {
          product_id: product.id,
          product_name: product.name,
          product_sku: product.sku,
          product_brand: product.brand || "-",
          product_status: product.status,
          opening_quantity: 0,
          unit_cost: parseFloat(product.cost) || 0,
          reorder_level: product.alert_quantity || 0,
          location: "",
          sku: product.sku || "",
          reason_notes: "",
          serial_numbers: [],
          meta: null,
          is_selected: !!rowSelection[product.id],
          tracking_type: product.tracking_type,
          is_serialized: product.is_serialized,
          sellable_qty: parseInt(product.sellable_qty) || 0,
          api_batches: product.batches || [],
          api_serial_numbers: product.serial_numbers || [],
          is_disabled: isDisabled,
          disabled_reason: disabledReason,
        };
      });
      setProductItems(items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsData]);

  // Mutations
  const createOpeningStockMutation = useMutation({
    mutationFn: async (payload) => {
      return await apiClient.post(API_ENDPOINTS.STOCK.OPENING_BALANCE, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["products-manage-inventory"],
      });
      toast.success("Opening stock created successfully");
      setNotes("");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create opening stock",
      );
    },
  });

  // Check if product already has inventory based on batches or serial numbers from the API
  const hasInventory = (productId) => {
    const product = productItems.find((item) => item.product_id === productId);
    if (!product) return false;
    // We check the API's original data if possible, or use the mapped fields
    return (
      (product.api_batches && product.api_batches.length > 0) ||
      (product.api_serial_numbers && product.api_serial_numbers.length > 0) ||
      (product.is_disabled &&
        product.disabled_reason === "Already has inventory record")
    );
  };

  // Serial Modal State
  const [serialModal, setSerialModal] = useState({
    isOpen: false,
    productId: null,
    productName: "",
    quantity: 0,
    initialSerials: [],
  });

  const openSerialModal = (record) => {
    setSerialModal({
      isOpen: true,
      productId: record.product_id,
      productName: record.product_name || record.name || "Product",
      // For serialized products in opening stock, we don't have a pre-defined quantity limit
      quantity: record.is_serialized
        ? 999999
        : parseInt(record.opening_quantity) || 0,
      initialSerials: record.serial_numbers || [],
    });
  };

  const handleSaveSerials = (serials) => {
    handleProductChange(serialModal.productId, "serial_numbers", serials);
  };

  // Handle product field change.
  // IMPORTANT: auto-select state is computed BEFORE the async setState updater so
  // that setRowSelection can be called synchronously in the same event batch.
  const handleProductChange = useCallback((productId, field, value) => {
    // Compute selection change synchronously, before the async updater runs.
    let autoSelect = null; // null = no change, true = select, false = deselect
    if (field === "opening_quantity") {
      autoSelect = parseInt(value) > 0;
    } else if (field === "serial_numbers") {
      autoSelect = (Array.isArray(value) ? value.length : 0) > 0;
    }

    setProductItems((prev) =>
      prev.map((item) => {
        if (item.product_id !== productId) return item;
        const updatedItem = { ...item, [field]: value };
        if (autoSelect !== null) updatedItem.is_selected = autoSelect;
        // Sync qty for serialized products when serials are saved
        if (field === "serial_numbers" && item.is_serialized) {
          updatedItem.opening_quantity = Array.isArray(value)
            ? value.length
            : 0;
        }
        return updatedItem;
      }),
    );

    if (autoSelect === true) {
      setRowSelection((prev) => ({ ...prev, [productId]: true }));
    } else if (autoSelect === false) {
      setRowSelection((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  }, []);

  // Handle checkbox selection
  const handleCheckboxChange = (productId, checked) => {
    setProductItems((prev) =>
      prev.map((item) => {
        if (item.product_id === productId) {
          return {
            ...item,
            is_selected: checked,
            opening_quantity: checked ? item.opening_quantity : 0,
          };
        }
        return item;
      }),
    );
  };

  // Use the items directly from the API as they are already paginated
  const pagedProducts = useMemo(() => {
    return productItems;
  }, [productItems]);

  // Use TanStack Table columns configuration
  const columns = useMemo(
    () => [
      {
        id: "select",
        header: "",
        cell: ({ row }) => {
          const record = row.original;
          const isDisabled = record.is_disabled;
          const isQtySet =
            (parseInt(record.opening_quantity) || 0) > 0 ||
            (record.serial_numbers && record.serial_numbers.length > 0);

          return isDisabled ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center">
                    <Checkbox disabled checked={false} />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{record.disabled_reason}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <div className="flex items-center justify-center">
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(checked) => {
                  row.toggleSelected(!!checked);
                  handleCheckboxChange(record.product_id, !!checked);
                }}
                disabled={isQtySet}
              />
            </div>
          );
        },
        size: 50,
      },
      {
        accessorKey: "product_name",
        header: "Product",
        cell: ({ row }) => {
          const record = row.original;
          const text = record.product_name;
          const isDisabled = record.is_disabled;
          return (
            <div className="flex flex-col">
              <span className="font-medium">{text}</span>
              {isDisabled && (
                <Badge
                  variant={
                    record.disabled_reason === "Already has inventory record"
                      ? "warning"
                      : "destructive"
                  }
                  className="w-fit mt-1 text-[10px] px-1 py-0 h-4"
                >
                  {record.disabled_reason === "Already has inventory record"
                    ? "Has Inventory"
                    : record.disabled_reason}
                </Badge>
              )}
            </div>
          );
        },
        size: 250,
      },
      {
        accessorKey: "sku",
        header: "SKU",
        cell: ({ row }) => {
          const record = row.original;
          const text = record.sku;
          const isDisabled = record.is_disabled;
          return (
            <EditableCellInput
              value={text || ""}
              onChange={(v) => handleProductChange(record.product_id, "sku", v)}
              maxLength={255}
              disabled={isDisabled}
              className="h-8 text-xs"
            />
          );
        },
        size: 150,
      },
      {
        accessorKey: "opening_quantity",
        header: () => (
          <span className="flex items-center gap-1">
            Opening Qty <span className="text-destructive">*</span>
          </span>
        ),
        cell: ({ row }) => {
          const record = row.original;
          const text = record.opening_quantity;
          const isDisabled = record.is_disabled;

          if (record.is_serialized) {
            return (
              <div className="flex flex-col gap-1">
                {isDisabled ? (
                  <span className="text-xs text-muted-foreground text-center">
                    {text} Units
                  </span>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openSerialModal(record)}
                    className={`w-full h-8 px-2 text-[10px] gap-1 font-bold transition-colors ${(record.serial_numbers?.length || 0) > 0
                      ? "bg-green-600 text-white border-green-700 hover:bg-green-700 hover:text-white"
                      : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:text-blue-800"
                      }`}
                  >
                    <ScanBarcode className="h-3 w-3" />
                    {record.serial_numbers?.length || 0} Serials
                  </Button>
                )}
              </div>
            );
          }

          return (
            <EditableCellInput
              type="number"
              value={text}
              onChange={(v) =>
                handleProductChange(record.product_id, "opening_quantity", v)
              }
              min={0}
              max={999999}
              disabled={isDisabled}
              className="h-8 text-right text-xs"
            />
          );
        },
        size: 120,
      },
      {
        accessorKey: "unit_cost",
        header: `Unit Cost (${currencySymbol})`,
        cell: ({ row }) => {
          const record = row.original;
          const text = record.unit_cost;
          const isDisabled = record.is_disabled;
          return (
            <EditableCellInput
              type="number"
              value={text}
              onChange={(v) =>
                handleProductChange(record.product_id, "unit_cost", v)
              }
              min={0}
              max={999999.99}
              step={0.01}
              disabled={isDisabled}
              className="h-8 text-right text-xs"
            />
          );
        },
        size: 120,
      },
      {
        accessorKey: "reorder_level",
        header: "Reorder Level",
        cell: ({ row }) => {
          const record = row.original;
          const text = record.reorder_level;
          const isDisabled = record.is_disabled;
          return (
            <EditableCellInput
              type="number"
              value={text}
              onChange={(v) =>
                handleProductChange(record.product_id, "reorder_level", v)
              }
              min={0}
              max={999999}
              disabled={isDisabled}
              className="h-8 text-right text-xs"
            />
          );
        },
        size: 120,
      },
      {
        accessorKey: "location",
        header: "Location",
        cell: ({ row }) => {
          const record = row.original;
          const text = record.location;
          const isDisabled = record.is_disabled;
          return (
            <EditableCellInput
              value={text || ""}
              onChange={(v) =>
                handleProductChange(record.product_id, "location", v)
              }
              maxLength={255}
              disabled={isDisabled}
              className="h-8 text-xs"
            />
          );
        },
        size: 150,
      },
      {
        accessorKey: "reason_notes",
        header: "Notes",
        cell: ({ row }) => {
          const record = row.original;
          const text = record.reason_notes;
          const isDisabled = record.is_disabled;
          return (
            <EditableCellInput
              value={text || (isDisabled ? "" : "Stock in hand")}
              onChange={(v) =>
                handleProductChange(record.product_id, "reason_notes", v)
              }
              maxLength={500}
              disabled={isDisabled}
              className="h-8 text-xs"
            />
          );
        },
        size: 200,
      },
      {
        accessorKey: "product_status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            variant={
              row.original.product_status === "active"
                ? "success"
                : "destructive"
            }
            className="capitalize text-[10px] px-1.5 py-0"
          >
            {row.original.product_status === "active" ? "Active" : "Inactive"}
          </Badge>
        ),
        size: 100,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currencySymbol],
  );

  // Validate form
  const validateForm = () => {
    if (!currentStore?.id) {
      toast.error("Store is required");
      return false;
    }

    const selectedItems = productItems.filter(
      (item) => item.is_selected && !hasInventory(item.product_id),
    );

    if (selectedItems.length === 0) {
      toast.error("Please select at least one product with opening quantity");
      return false;
    }

    if (selectedItems.length > 100) {
      toast.error("Maximum 100 products allowed per submission");
      return false;
    }

    for (let i = 0; i < selectedItems.length; i++) {
      const item = selectedItems[i];
      if (!item.product_id) {
        toast.error(`Product at row ${i + 1} is required`);
        return false;
      }

      // Validate tracking type
      if (
        item.tracking_type !== "standard" &&
        item.tracking_type !== "serial"
      ) {
        toast.error(
          `Product "${item.product_name || "at row " + (i + 1)}" has an invalid tracking type for opening stock`,
        );
        return false;
      }

      // Validate quantity is greater than 0
      const qty = parseInt(item.opening_quantity);
      if (isNaN(qty) || qty <= 0) {
        toast.error(
          `Opening quantity for "${item.product_name || "product at row " + (i + 1)}" must be greater than 0`,
        );
        return false;
      }

      // If serialized, validate serial numbers count matches quantity
      if (item.tracking_type === "serial") {
        if (!item.serial_numbers || item.serial_numbers.length === 0) {
          toast.error(
            `Please add serial numbers for "${item.product_name || "product at row " + (i + 1)}"`,
          );
          return false;
        }
        if (item.serial_numbers.length !== qty) {
          toast.error(
            `Number of serial numbers (${item.serial_numbers.length}) does not match opening quantity (${qty}) for "${item.product_name || "product at row " + (i + 1)}"`,
          );
          return false;
        }
      }
    }

    return true;
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const selectedItems = productItems.filter(
      (item) => item.is_selected && !hasInventory(item.product_id),
    );

    const payload = {
      store_id: currentStore.id,
      notes: notes || null,
      items: selectedItems.map((item) => ({
        product_id: item.product_id,
        opening_quantity: parseInt(item.opening_quantity),
        unit_cost: item.unit_cost ? parseFloat(item.unit_cost) : null,
        reorder_level: item.reorder_level ? parseInt(item.reorder_level) : null,
        location: item.location || null,
        sku: item.sku || null,
        reason_notes: item.reason_notes || null,
        serial_numbers: item.serial_numbers || [],
        meta: item.meta || null,
      })),
    };

    createOpeningStockMutation.mutate(payload);
  };

  if (
    !hasPermission("inventory create") &&
    !hasPermission("inventory serial create") &&
    !hasPermission("inventory serial bulk_create")
  ) {
    return <RecordNotFound />;
  }

  return (
    <div className="bg-muted/10 min-h-[calc(100vh-4rem)]">
      {/* Top Header */}
      <div className="sticky top-16 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm">
        <div className="px-4 md:px-6 lg:px-8 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild className="-ml-2">
              <Link to={all_routes.productlist}>
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-foreground">
                Opening Stock
              </h1>
              <p className="text-sm text-muted-foreground">
                Add initial stock levels for products that don't have inventory
                yet
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" asChild className="gap-2">
              <Link to={all_routes.productlist}>Cancel</Link>
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={
                createOpeningStockMutation.isPending ||
                !productItems.some(
                  (item) => item.is_selected && !hasInventory(item.product_id),
                ) ||
                (!hasPermission("inventory create") &&
                  !hasPermission("inventory serial bulk_create"))
              }
              className="gap-2 shadow-sm"
            >
              {createOpeningStockMutation.isPending ? (
                <Spinner className="h-4 w-4" />
              ) : (
                <PlusCircle className="h-4 w-4" />
              )}
              Save Opening Stock
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 lg:p-8 w-full max-w-full animate-in fade-in duration-500">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card className="md:col-span-1 h-full">
            <CardHeader className="py-4">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <Package className="h-4 w-4 text-primary" />
                Store Context
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-bold text-muted-foreground tracking-wider">
                  Current Store
                </Label>
                <Input
                  type="text"
                  value={currentStore?.name || "Loading..."}
                  disabled
                  className="bg-muted font-semibold"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-bold text-muted-foreground tracking-wider">
                  Adjustment Notes
                </Label>
                <Textarea
                  rows={4}
                  placeholder="Enter reason for this opening stock... (e.g. Migration from old system)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={1000}
                  className="resize-none"
                />
                <p className="text-[10px] text-right text-muted-foreground">
                  {notes.length}/1000
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="md:col-span-2 h-full">
            <CardHeader className="py-4">
              <CardTitle className="text-base font-bold flex items-center gap-2">
                <Info className="h-4 w-4 text-primary" />
                Instructions & Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Alert className="bg-primary/5 border-primary/20">
                <Info className="h-4 w-4 text-primary" />
                <AlertDescription>
                  <ul className="text-xs space-y-2 text-foreground font-medium">
                    <li className="flex gap-2">
                      <span className="text-primary font-black flex-shrink-0">
                        1.
                      </span>
                      Products already having inventory in{" "}
                      <strong>{currentStore?.name}</strong> are disabled.
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary font-black flex-shrink-0">
                        2.
                      </span>
                      Enter the <strong>Opening Qty</strong> and{" "}
                      <strong>Unit Cost</strong> to create initial inventory and
                      batch.
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary font-black flex-shrink-0">
                        3.
                      </span>
                      You can specify a <strong>Storage Location</strong> (e.g.,
                      Aisle 4, Shelf B) for organization.
                    </li>
                    <li className="flex gap-2">
                      <span className="text-primary font-black flex-shrink-0">
                        4.
                      </span>
                      This action is permanent and creates a "Migration" audit
                      trail.
                    </li>
                  </ul>
                </AlertDescription>
              </Alert>
              <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/20">
                <div className="text-sm font-medium">Selected for import:</div>
                <Badge variant="success" className="font-black text-xs">
                  {
                    productItems.filter(
                      (item) =>
                        item.is_selected && !hasInventory(item.product_id),
                    ).length
                  }{" "}
                  Products
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        <FilterCard>
          <FilterSearch
            placeholder="Filter products by name or SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="flex items-center gap-2 shrink-0">
            <Select value={hasInventoryRec} onValueChange={setHasInventoryRec}>
              <SelectTrigger className="w-[240px] h-10 bg-background">
                <SelectValue placeholder="Filter by inventory" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                <SelectItem value="has_inventory_rec">
                  With Existing Inventory
                </SelectItem>
                <SelectItem value="no_inventory_rec">
                  Without Existing Inventory
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </FilterCard>

        <TableCard>
          <DataTable
            columns={columns}
            data={pagedProducts}
            isLoading={
              isProductsLoading || (!currentStore?.id && !productItems.length)
            }
            enableRowSelection={true}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            getRowId={(row) => row.product_id}
            totalRows={productsData?.total || 0}
            itemLabel="products"
            pageSizeOptions={[10, 15, 25, 50, 100]}
            pageCount={
              productsData?.last_page ||
              Math.max(1, Math.ceil((productsData?.total || 0) / pageSize))
            }
            pagination={{
              pageIndex: currentPage - 1,
              pageSize: pageSize,
            }}
            onPaginationChange={(updater) => {
              const oldState = {
                pageIndex: currentPage - 1,
                pageSize,
              };
              const newState =
                typeof updater === "function" ? updater(oldState) : updater;
              if (
                newState.pageSize !== undefined &&
                newState.pageSize !== pageSize
              ) {
                setPageSize(newState.pageSize);
                setCurrentPage(1);
              } else if (newState.pageIndex !== undefined) {
                setCurrentPage(newState.pageIndex + 1);
              }
            }}
          />
        </TableCard>

        {/* Add Serials Modal */}
        <AddSerialsModal
          isOpen={serialModal.isOpen}
          onClose={() => setSerialModal((prev) => ({ ...prev, isOpen: false }))}
          onSave={handleSaveSerials}
          initialSerials={serialModal.initialSerials}
          quantity={serialModal.quantity}
          productName={serialModal.productName}
        />
      </div>
    </div>
  );
};

export default OpeningStock;

